import { useCallback, useMemo } from 'react';
import { skipToken, useQueries, UseQueryResult } from '@tanstack/react-query';
import { QueryResultType, StatisticsItemType, UseMultiSuggestStatisticsProps } from '@lp/features/MultiSuggest';
import { generateStatisticsItems } from '@lp/features/MultiSuggest/utils';
import { RouteProjectionType, RouteTypeEnum } from '@/api/apis/route';
import { routePlannerQueryKeys } from '@/queries/route-planner/route-planner.keys';
import { MultiSuggestRoutePlanType } from '@/utils/route/chains';
import { multiSuggestKeys } from '@/queries/multi-suggest';

type UseMultiSuggestStatisticsReturnType = {
  statisticsItems: StatisticsItemType[];
  isLoading: boolean;
};

export const useMultiSuggestStatistics = ({
  taskId,
  caseIds,
  logisticsIds,
  chainNumber,
}: UseMultiSuggestStatisticsProps): UseMultiSuggestStatisticsReturnType => {
  const logisticsRouteList = useQueries<RouteProjectionType[], QueryResultType<RouteProjectionType>[]>({
    queries: logisticsIds.map((id) => ({
      queryKey: routePlannerQueryKeys.getRouteByType(id, RouteTypeEnum.Main),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({ data: r.data, isLoading: r.isPending })) as QueryResultType<RouteProjectionType>[],
      [],
    ),
  });

  const suggestedList = useQueries<
    (MultiSuggestRoutePlanType | null)[],
    QueryResultType<MultiSuggestRoutePlanType | null>[]
  >({
    queries: caseIds.map((id) => ({
      queryKey: multiSuggestKeys.getMultiSuggestCaseResultByCase(taskId, chainNumber, id),
      queryFn: skipToken,
    })),
    combine: useCallback(
      (result: UseQueryResult[]) =>
        result.map((r) => ({
          data: r.data,
          isLoading: r.isPending,
        })) as QueryResultType<MultiSuggestRoutePlanType | null>[],
      [],
    ),
  });

  const isLoading = useMemo(() => {
    const isLogisticsLoading = logisticsRouteList.some((r) => r.isLoading);
    const isSuggestedLoading = suggestedList.some((r) => r.isLoading);

    return isLogisticsLoading || isSuggestedLoading;
  }, [logisticsRouteList, suggestedList]);

  const combinedLogisticsLegs = useMemo(() => {
    if (isLoading) return { legs: [], accommodationLegs: [] };

    return logisticsRouteList.reduce<Pick<RouteProjectionType, 'legs' | 'accommodationLegs'>>(
      (acc, item) => {
        if (!item.data) return acc;

        return {
          legs: [...acc.legs, ...item.data.legs],
          accommodationLegs: [...acc.accommodationLegs, ...item.data.accommodationLegs],
        };
      },
      { legs: [], accommodationLegs: [] },
    );
  }, [logisticsRouteList, isLoading]);

  const emptyLogisticsCaseIds = useMemo(() => {
    if (logisticsRouteList.length === 1) return [];

    return logisticsRouteList
      .filter((r) => !r.data.legs.length && !r.data.accommodationLegs.length)
      .map((r) => r.data?.caseId)
      .filter((id): id is string => Boolean(id));
  }, [logisticsRouteList]);

  const combinedSuggestedLegs = useMemo(() => {
    if (isLoading) return { legs: [], accommodationLegs: [] };

    const list = suggestedList.filter((item) => !!item.data && !emptyLogisticsCaseIds.includes(item.data.caseId));

    return list.reduce<Pick<RouteProjectionType, 'legs' | 'accommodationLegs'>>(
      (acc, item) => {
        if (!item.data) return acc;

        const accommodationList = Array.from(item.data.accommodationLegs.values()).flat();
        const legsList = Array.from(item.data.legs.values()).flat();

        return {
          legs: [...acc.legs, ...legsList],
          accommodationLegs: [...acc.accommodationLegs, ...accommodationList],
        };
      },
      { legs: [], accommodationLegs: [] },
    );
  }, [emptyLogisticsCaseIds, suggestedList, isLoading]);

  const statisticsItems = useMemo(() => {
    const hasSuggestions = suggestedList.some((d) => d.data);
    if (isLoading || !hasSuggestions) return [];

    return generateStatisticsItems(combinedLogisticsLegs, combinedSuggestedLegs);
  }, [combinedLogisticsLegs, combinedSuggestedLegs, suggestedList, isLoading]);

  return {
    isLoading,
    statisticsItems,
  };
};
